import { makeApi, Zodios, type ZodiosOptions } from "@zodios/core";
import { z } from "zod";

const github_com_falqondev_reportdev_datalake_backend_service_ApplicationRole =
  z
    .object({
      aproID: z.string(),
      description: z.string(),
      name: z.string(),
      roles: z.array(z.string()),
    })
    .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_AuditLogView = z
  .object({
    action: z.string(),
    auloID: z.number(),
    createdAt: z.string(),
    createdBy: z.string(),
    resourceMetadata: z.unknown(),
    resourceName: z.string(),
    userInfo: z
      .object({
        avatar: z.string().nullable(),
        email: z.string().nullable(),
        name: z.string().nullable(),
      })
      .partial()
      .passthrough(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_CatalogColumn = z
  .object({
    beginSnapshot: z.number(),
    cacoID: z.string(),
    cataID: z.string(),
    columnName: z.string(),
    columnType: z.string().nullish(),
    endSnapshot: z.number().nullish(),
    nullsAllowed: z.boolean().nullish(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_CatalogTag = z
  .object({ ctagID: z.number(), slug: z.string(), tag: z.string() })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_CatalogEntity = z
  .object({
    beginSnapshot: z.number(),
    cascID: z.string(),
    cataID: z.string(),
    certification: z
      .object({
        certID: z.number(),
        description: z.string(),
        icon: z.string().nullish(),
        name: z.string(),
      })
      .passthrough()
      .nullish(),
    columns: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_CatalogColumn
    ),
    description: z.string(),
    domains: z.array(
      z.object({ domaID: z.number(), name: z.string() }).passthrough()
    ),
    endSnapshot: z.number().nullish(),
    entityType: z.string(),
    fullyQualifiedName: z.string(),
    layeID: z.number().nullish(),
    layerDescription: z.string().nullish(),
    layerName: z.string().nullish(),
    path: z.string().nullish(),
    schemaName: z.string(),
    tableName: z.string(),
    tags: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_CatalogTag
    ),
    temporary: z.boolean(),
  })
  .passthrough();
const catalogEntityUpdate_Body = z
  .object({
    certID: z.number().nullish(),
    description: z.string(),
    domains: z.array(z.number()),
    tags: z.array(z.number()),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_CatalogNavigationTable =
  z
    .object({ cataID: z.string(), fqn: z.string(), name: z.string() })
    .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_CatalogNavigationSchema =
  z
    .object({
      cascID: z.string(),
      fqn: z.string(),
      name: z.string(),
      tableCount: z.number(),
      tables: z
        .array(
          github_com_falqondev_reportdev_datalake_backend_service_CatalogNavigationTable
        )
        .nullish(),
    })
    .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_CatalogNavigationDatabase =
  z
    .object({
      displayName: z.string(),
      fqn: z.string(),
      name: z.string(),
      schemas: z.array(
        github_com_falqondev_reportdev_datalake_backend_service_CatalogNavigationSchema
      ),
    })
    .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_CatalogNavigationService =
  z
    .object({
      databases: z.array(
        github_com_falqondev_reportdev_datalake_backend_service_CatalogNavigationDatabase
      ),
      displayName: z.string(),
      fqn: z.string(),
      layeID: z.number().nullish(),
      name: z.string(),
    })
    .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_CatalogNavigation =
  z
    .object({
      serviceType: z.string().nullable(),
      services: z
        .array(
          github_com_falqondev_reportdev_datalake_backend_service_CatalogNavigationService
        )
        .nullable(),
    })
    .partial()
    .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_CertificationView =
  z
    .object({
      certID: z.number(),
      createdAt: z.string(),
      deletedAt: z.string().nullish(),
      description: z.string(),
      icon: z.string().nullish(),
      name: z.string(),
    })
    .passthrough();
const certificationCreate_Body = z
  .object({
    description: z.string().nullish(),
    icon: z.string().nullish(),
    name: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_ChartBlockView = z
  .object({
    cachedData: z.unknown(),
    chartDefinition: z.unknown(),
    chblID: z.string(),
    code: z.string(),
    createdAt: z.string(),
    deletedAt: z.string().nullish(),
    description: z.string(),
    lang: z.string(),
    name: z.string(),
    projID: z.string(),
    thumbnail: z.string(),
    updatedAt: z.string(),
    viewType: z.string().nullish(),
  })
  .passthrough();
const chartBlockCreate_Body = z
  .object({
    cachedData: z.unknown(),
    chartDefinition: z.unknown(),
    code: z.string(),
    description: z.string(),
    lang: z.string(),
    name: z.string(),
    projID: z.string(),
    thumbnail: z.string(),
    viewType: z.string().nullish(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_ConnectionSecretView =
  z
    .object({
      accessLevel: z.string(),
      connectString: z.string(),
      coseID: z.string(),
      createdAt: z.string(),
      credentials: z
        .object({
          accountID: z.string().nullish(),
          accountName: z.string().nullish(),
          chain: z.string().nullish(),
          clientCertificatePath: z.string().nullish(),
          clientID: z.string().nullish(),
          clientSecret: z.string().nullish(),
          database: z.string().nullish(),
          endpoint: z.string().nullish(),
          keyID: z.string().nullish(),
          kmsKeyID: z.string().nullish(),
          password: z.string().nullish(),
          provider: z.string().nullish(),
          region: z.string().nullish(),
          scope: z.string().nullish(),
          secret: z.string().nullish(),
          sessionToken: z.string().nullish(),
          tenantID: z.string().nullish(),
          type: z.string(),
          urlCompatibilityMode: z.boolean().nullish(),
          urlStyle: z.string().nullish(),
          useSsl: z.boolean().nullish(),
          user: z.string().nullish(),
        })
        .passthrough(),
      deletedAt: z.string().nullish(),
      hidden: z.boolean(),
      host: z.string().nullish(),
      name: z.string(),
      port: z.number().nullish(),
      readOnly: z.boolean(),
      ssh: z
        .object({
          enabled: z.boolean(),
          host: z.string().nullish(),
          passphrase: z.string().nullish(),
          port: z.number().nullish(),
          privateKey: z.string().nullish(),
          user: z.string().nullish(),
        })
        .passthrough()
        .nullish(),
      ssl: z
        .object({
          ca: z.string().nullish(),
          cert: z.string().nullish(),
          enabled: z.boolean(),
          key: z.string().nullish(),
          mode: z.string().nullish(),
        })
        .passthrough()
        .nullish(),
    })
    .passthrough();
const connectionSecretCreate_Body = z
  .object({
    accessLevel: z.string(),
    connectString: z.string().nullish(),
    credentials: z
      .object({
        accountID: z.string().nullish(),
        accountName: z.string().nullish(),
        chain: z.string().nullish(),
        clientCertificatePath: z.string().nullish(),
        clientID: z.string().nullish(),
        clientSecret: z.string().nullish(),
        database: z.string().nullish(),
        endpoint: z.string().nullish(),
        keyID: z.string().nullish(),
        kmsKeyID: z.string().nullish(),
        password: z.string().nullish(),
        provider: z.string().nullish(),
        region: z.string().nullish(),
        scope: z.string().nullish(),
        secret: z.string().nullish(),
        sessionToken: z.string().nullish(),
        tenantID: z.string().nullish(),
        type: z.string(),
        urlCompatibilityMode: z.boolean().nullish(),
        urlStyle: z.string().nullish(),
        useSsl: z.boolean().nullish(),
        user: z.string().nullish(),
      })
      .passthrough(),
    host: z.string().nullish(),
    name: z.string(),
    port: z.number().nullish(),
    ssh: z
      .object({
        enabled: z.boolean(),
        host: z.string().nullish(),
        passphrase: z.string().nullish(),
        port: z.number().nullish(),
        privateKey: z.string().nullish(),
        user: z.string().nullish(),
      })
      .passthrough()
      .nullish(),
    ssl: z
      .object({
        ca: z.string().nullish(),
        cert: z.string().nullish(),
        enabled: z.boolean(),
        key: z.string().nullish(),
        mode: z.string().nullish(),
      })
      .passthrough()
      .nullish(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_TableData = z
  .object({
    fqn: z.string(),
    objectType: z.string(),
    schemaName: z.string(),
    tableName: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_DashboardView = z
  .object({
    createdAt: z.string(),
    dashID: z.string(),
    data: z.unknown(),
    deletedAt: z.string().nullish(),
    name: z.string(),
    projID: z.string(),
  })
  .passthrough();
const dashboardCreate_Body = z
  .object({ data: z.unknown(), name: z.string(), projID: z.string() })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_DataAccessGroupView =
  z
    .object({
      actions: z.array(z.string()),
      createdAt: z.string(),
      dagrID: z.string(),
      deletedAt: z.string().nullish(),
      description: z.string(),
      name: z.string(),
    })
    .passthrough();
const dataAccessGroupCreate_Body = z
  .object({
    actions: z.array(z.string()),
    description: z.string(),
    name: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_DocumentationView =
  z
    .object({
      content: z.string(),
      createdAt: z.string(),
      docuID: z.number(),
      tags: z.array(z.string()),
      title: z.string(),
      videoURL: z.string(),
    })
    .passthrough();
const documentationCreate_Body = z
  .object({
    content: z.string(),
    tags: z.array(z.string()).nullish(),
    title: z.string(),
    videoURL: z.string().nullish(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_Document = z
  .object({
    createdAt: z.string(),
    data: z.unknown(),
    deletedAt: z.string().nullish(),
    description: z.string(),
    docuID: z.string(),
    doteID: z.string(),
    formSchema: z.unknown(),
    permissionMode: z.string(),
    printSchema: z.unknown(),
    title: z.string(),
    uiSchema: z.unknown(),
    updatedAt: z.string(),
  })
  .passthrough();
const documentCreate_Body = z
  .object({
    data: z.unknown(),
    description: z.string(),
    doteID: z.string(),
    formSchema: z.unknown(),
    permissionMode: z.string(),
    printSchema: z.unknown(),
    title: z.string(),
    uiSchema: z.unknown(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_DomainView = z
  .object({
    createdAt: z.string(),
    deletedAt: z.string().nullish(),
    description: z.string(),
    domaID: z.number(),
    name: z.string(),
    parentID: z.number().nullish(),
  })
  .passthrough();
const domainCreate_Body = z
  .object({
    description: z.string().nullish(),
    name: z.string(),
    parentID: z.number().nullish(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_ErrorReportView =
  z
    .object({
      createdAt: z.string(),
      erreID: z.number(),
      filterRegex: z.string(),
      name: z.string(),
      notifiedUsers: z.array(z.string()),
      updatedAt: z.string(),
    })
    .passthrough();
const errorReportCreate_Body = z
  .object({
    filterRegex: z.string(),
    name: z.string(),
    notifiedUsers: z.array(z.string()),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_LayerConnection =
  z
    .object({
      coseID: z.string(),
      database: z.string().nullish(),
      name: z.string(),
      scope: z.string().nullish(),
    })
    .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_LayerView = z
  .object({
    connections: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_LayerConnection
    ),
    coseID: z.string(),
    createdAt: z.string(),
    deletedAt: z.string().nullish(),
    description: z.string(),
    displayOrder: z.number(),
    layeID: z.number(),
    name: z.string(),
    type: z.string(),
  })
  .passthrough();
const layerCreate_Body = z
  .object({
    description: z.string(),
    displayOrder: z.number(),
    name: z.string(),
    type: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_EdgeView = z
  .object({
    description: z.string().nullish(),
    doc_id: z.string().nullish(),
    fromEntity: z
      .object({
        fqn: z.string(),
        fullyQualifiedNameHash: z.string().nullish(),
        id: z.string(),
        type: z.string(),
      })
      .passthrough(),
    pipeline: z
      .object({
        description: z.string().nullish(),
        displayName: z.string(),
        id: z.string(),
        name: z.string(),
      })
      .passthrough(),
    source: z.string().nullish(),
    toEntity: z
      .object({
        fqn: z.string(),
        fullyQualifiedNameHash: z.string().nullish(),
        id: z.string(),
        type: z.string(),
      })
      .passthrough(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_EntityView = z
  .object({
    database: z
      .object({
        description: z.string(),
        displayName: z.string(),
        fullyQualifiedName: z.string(),
        id: z.string(),
        name: z.string(),
        type: z.string(),
      })
      .passthrough()
      .nullish(),
    databaseSchema: z
      .object({
        description: z.string(),
        displayName: z.string(),
        fullyQualifiedName: z.string(),
        id: z.string(),
        name: z.string(),
        type: z.string(),
      })
      .passthrough()
      .nullish(),
    description: z.string().nullish(),
    displayName: z.string().nullish(),
    entityType: z.string(),
    fullyQualifiedName: z.string(),
    id: z.string(),
    lineage: z
      .array(github_com_falqondev_reportdev_datalake_backend_service_EdgeView)
      .nullish(),
    name: z.string(),
    service: z
      .object({
        description: z.string(),
        displayName: z.string(),
        fullyQualifiedName: z.string(),
        id: z.string(),
        name: z.string(),
        type: z.string(),
      })
      .passthrough()
      .nullish(),
    serviceType: z.string().nullish(),
    tableColumns: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_CatalogColumn
    ),
    tableType: z.string().nullish(),
    tags: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_CatalogTag
    ),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_OauthIntegrationView =
  z
    .object({
      authScript: z.string(),
      callbackScript: z.string(),
      createdAt: z.string(),
      credential: z
        .object({
          clientID: z.string().nullable(),
          clientSecret: z.string().nullable(),
        })
        .partial()
        .passthrough(),
      deletedAt: z.string().nullish(),
      name: z.string(),
      oainID: z.number(),
      refreshToken: z.string(),
      type: z.string(),
      userData: z
        .object({ user_email: z.string().nullable() })
        .partial()
        .passthrough()
        .nullish(),
    })
    .passthrough();
const oauthIntegrationCreate_Body = z
  .object({
    credential: z
      .object({
        clientID: z.string().nullable(),
        clientSecret: z.string().nullable(),
      })
      .partial()
      .passthrough(),
    name: z.string(),
    refreshtoken: z.string(),
    type: z.string(),
  })
  .passthrough();
const oneTimeLoginCreate_Body = z
  .object({ userID: z.string(), validUntil: z.string() })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_PlaygroundCode = z
  .object({
    code: z.string(),
    database: z.string(),
    executionOrder: z.number(),
    lang: z.string(),
    resourceID: z.string().nullish(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_PlaygroundTask = z
  .object({
    createdAt: z.string(),
    payload: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_PlaygroundCode
    ),
    plseID: z.string(),
    response: z.array(z.object({}).partial().passthrough()).nullish(),
    runID: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_PlaygroundSessionView =
  z
    .object({
      createdAt: z.string(),
      expiresAt: z.string(),
      plseID: z.string(),
      resourceID: z.string(),
      resourceType: z.string(),
      tasks: z.array(
        github_com_falqondev_reportdev_datalake_backend_service_PlaygroundTask
      ),
    })
    .passthrough();
const playgroundSessionCreate_Body = z
  .object({ resourceID: z.string(), resourceType: z.string() })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_ColumnInfo = z
  .object({
    defaultVal: z.string(),
    name: z.string(),
    nullable: z.boolean(),
    type: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_TableInfo = z
  .object({
    columns: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_ColumnInfo
    ),
    name: z.string(),
    rows: z.number(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_SchemaInfo = z
  .object({
    name: z.string(),
    tables: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_TableInfo
    ),
  })
  .passthrough();
const playgroundRunCreate_Body = z
  .object({
    arguments: z.unknown().nullish(),
    databases: z.array(z.string()),
    executionGroup: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_PlaygroundCode
    ),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_ProjectView = z
  .object({
    createdAt: z.string(),
    deletedAt: z.string().nullish(),
    name: z.string(),
    projID: z.string(),
    updatedAt: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_ReportView = z
  .object({
    attachments: z
      .object({
        chartBlocks: z.array(z.string()),
        dashboards: z.array(z.string()),
      })
      .passthrough(),
    chblID: z.string().nullish(),
    createdAt: z.string(),
    deletedAt: z.string().nullish(),
    description: z.string(),
    enabled: z.boolean(),
    frequency: z.string(),
    message: z.string(),
    name: z.string(),
    oainID: z.number().nullish(),
    projID: z.string(),
    reatID: z.string(),
    recipients: z.array(z.string()),
    repoID: z.string(),
    reportFailureEmail: z.array(z.string()),
    sendMethod: z.string(),
    sendingIndividual: z.boolean(),
    subject: z.string(),
    templateRejectionReason: z.string().nullish(),
    templateSid: z.string().nullish(),
    templateStatus: z.string().nullish(),
    timezone: z.string(),
    useTextAttachments: z.boolean(),
  })
  .passthrough();
const reportCreate_Body = z
  .object({
    attachments: z
      .object({
        chartBlocks: z.array(z.string()),
        dashboards: z.array(z.string()),
      })
      .passthrough(),
    chblID: z.string().nullish(),
    description: z.string(),
    enabled: z.boolean(),
    frequency: z.string(),
    message: z.string(),
    name: z.string(),
    oainID: z.number().nullish(),
    projID: z.string(),
    recipients: z.array(z.string()),
    reportFailureEmail: z.array(z.string()),
    sendMethod: z.string(),
    sendingIndividual: z.boolean(),
    subject: z.string(),
    timezone: z.string(),
    useTextAttachments: z.boolean(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_PendingTerm = z
  .object({ slug: z.string(), termID: z.number(), title: z.string() })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_Term = z
  .object({
    createdAt: z.string(),
    deletedAt: z.string().nullish(),
    description: z.string(),
    slug: z.string(),
    termID: z.number(),
    title: z.string(),
    type: z.string(),
  })
  .passthrough();
const termCreate_Body = z
  .object({
    description: z.string(),
    slug: z.string(),
    title: z.string(),
    type: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_TestSuitData = z
  .object({ name: z.string(), tesuID: z.number() })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_TestView = z
  .object({
    createdAt: z.string(),
    deletedAt: z.string().nullish(),
    description: z.string(),
    fullQualifiedName: z.string().nullish(),
    lastRun: z.string().nullish(),
    name: z.string(),
    suites: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_TestSuitData
    ),
    testID: z.string(),
  })
  .passthrough();
const testCreate_Body = z
  .object({
    description: z.string(),
    name: z.string(),
    tesuIDs: z.array(z.number()),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_TestSuiteView = z
  .object({
    createdAt: z.string(),
    deletedAt: z.string().nullish(),
    description: z.string(),
    frequency: z.string(),
    name: z.string(),
    schedulingEnabled: z.boolean(),
    testIDs: z.array(z.string()),
    tesuID: z.number(),
    timezone: z.string(),
  })
  .passthrough();
const testSuiteCreate_Body = z
  .object({
    description: z.string(),
    frequency: z.string(),
    name: z.string(),
    schedulingEnabled: z.boolean(),
    testIDs: z.array(z.string()),
    timezone: z.string(),
  })
  .passthrough();
const testCodeBlockCreate_Body = z
  .object({ code: z.string(), fullQualifiedName: z.string(), lang: z.string() })
  .passthrough();
const userRoleCreate_Body = z
  .object({ aproIDs: z.array(z.string()), userID: z.string() })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_UserInformation =
  z
    .object({
      applID: z.string(),
      auth2fa: z
        .object({
          methods: z.array(z.string()).nullish(),
          required: z.boolean(),
        })
        .passthrough(),
      authenticationProvider: z.string(),
      avatar: z.string().nullish(),
      createdAt: z.string(),
      dataAccessRoles: z.array(z.string()),
      deletedAt: z.string().nullish(),
      email: z.string().nullish(),
      info: z.object({}).partial().passthrough(),
      name: z.string(),
      pushTokens: z.array(z.string()).nullish(),
      roles: z.array(z.string()),
      userID: z.string(),
    })
    .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_InstitutionUserCreateFormByUser =
  z
    .object({
      admittedAt: z.string().nullish(),
      buunID: z.number().nullish(),
      code: z.string().nullish(),
      instID: z.string(),
      operationalCode: z.string().nullish(),
      phone: z.string(),
      sex: z.string(),
    })
    .passthrough();
const userCreate_Body = z
  .object({
    applID: z.string(),
    avatar: z.string().nullish(),
    email: z.string(),
    institutions: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_InstitutionUserCreateFormByUser
    ),
    name: z.string(),
    password: z.string().nullish(),
  })
  .passthrough();
const userUpdate_Body = z
  .object({
    auth2fa: z
      .object({ methods: z.array(z.string()).nullish(), required: z.boolean() })
      .passthrough()
      .nullable(),
    avatar: z.string().nullable(),
    email: z.string().nullable(),
    name: z.string().nullable(),
  })
  .partial()
  .passthrough();
const userDataAccessGroupCreate_Body = z
  .object({ rules: z.array(z.string()) })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_Argument = z
  .object({ arguID: z.string(), data: z.array(z.string()), name: z.string() })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_RootInformation =
  z.object({ frequency: z.string(), workID: z.string() }).passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_CodeBlock = z
  .object({
    coblID: z.string(),
    code: z.string(),
    createdAt: z.string(),
    database: z.string(),
    description: z.string(),
    executionOrder: z.number(),
    hash: z.string(),
    lang: z.string(),
    name: z.string(),
    parentID: z.string().nullish(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_WorkflowView = z
  .object({
    arguments: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_Argument
    ),
    createdAt: z.string(),
    databases: z.array(z.string()),
    deletedAt: z.string().nullish(),
    dependsWorkIDs: z.array(z.string()),
    deployedAt: z.string().nullish(),
    description: z.string(),
    frequency: z.string(),
    hash: z.string(),
    name: z.string(),
    parentNames: z.array(z.string()),
    roots: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_RootInformation
    ),
    schedulingEnabled: z.boolean(),
    scripts: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_CodeBlock
    ),
    tag: z.string(),
    timezone: z.string(),
    wagrID: z.string().nullish(),
    wcegID: z.string(),
    wodeID: z.number().nullish(),
    workID: z.string(),
    woveID: z.number(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_ArgumentForm = z
  .object({
    arguID: z.string().nullish(),
    data: z.array(z.string()),
    name: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_CodeBlocksForm = z
  .object({
    coblID: z.string().nullish(),
    code: z.string(),
    database: z.string(),
    description: z.string(),
    executionOrder: z.number(),
    lang: z.string(),
    name: z.string(),
    parentID: z.string().nullish(),
  })
  .passthrough();
const workflowCreate_Body = z
  .object({
    arguments: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_ArgumentForm
    ),
    databases: z.array(z.string()),
    dependsWorkIDs: z.array(z.string()).nullish(),
    deploy: z.boolean().nullish(),
    description: z.string().nullish(),
    frequency: z.string(),
    name: z.string(),
    schedulingEnabled: z.boolean(),
    scripts: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_CodeBlocksForm
    ),
    tag: z.string().nullish(),
    timezone: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_WorkflowParent = z
  .object({
    forbiddenParents: z.array(z.string()),
    name: z.string(),
    roots: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_RootInformation
    ),
    workID: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_WorkflowVersionHistory =
  z
    .object({
      createdAt: z.string(),
      hash: z.string(),
      wodeID: z.number().nullish(),
      workID: z.string(),
      woveID: z.number(),
    })
    .passthrough();

export const schemas = {
  github_com_falqondev_reportdev_datalake_backend_service_ApplicationRole,
  github_com_falqondev_reportdev_datalake_backend_service_AuditLogView,
  github_com_falqondev_reportdev_datalake_backend_service_CatalogColumn,
  github_com_falqondev_reportdev_datalake_backend_service_CatalogTag,
  github_com_falqondev_reportdev_datalake_backend_service_CatalogEntity,
  catalogEntityUpdate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_CatalogNavigationTable,
  github_com_falqondev_reportdev_datalake_backend_service_CatalogNavigationSchema,
  github_com_falqondev_reportdev_datalake_backend_service_CatalogNavigationDatabase,
  github_com_falqondev_reportdev_datalake_backend_service_CatalogNavigationService,
  github_com_falqondev_reportdev_datalake_backend_service_CatalogNavigation,
  github_com_falqondev_reportdev_datalake_backend_service_CertificationView,
  certificationCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_ChartBlockView,
  chartBlockCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_ConnectionSecretView,
  connectionSecretCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_TableData,
  github_com_falqondev_reportdev_datalake_backend_service_DashboardView,
  dashboardCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_DataAccessGroupView,
  dataAccessGroupCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_DocumentationView,
  documentationCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_Document,
  documentCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_DomainView,
  domainCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_ErrorReportView,
  errorReportCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_LayerConnection,
  github_com_falqondev_reportdev_datalake_backend_service_LayerView,
  layerCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_EdgeView,
  github_com_falqondev_reportdev_datalake_backend_service_EntityView,
  github_com_falqondev_reportdev_datalake_backend_service_OauthIntegrationView,
  oauthIntegrationCreate_Body,
  oneTimeLoginCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundCode,
  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundTask,
  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundSessionView,
  playgroundSessionCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_ColumnInfo,
  github_com_falqondev_reportdev_datalake_backend_service_TableInfo,
  github_com_falqondev_reportdev_datalake_backend_service_SchemaInfo,
  playgroundRunCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_ProjectView,
  github_com_falqondev_reportdev_datalake_backend_service_ReportView,
  reportCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_PendingTerm,
  github_com_falqondev_reportdev_datalake_backend_service_Term,
  termCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_TestSuitData,
  github_com_falqondev_reportdev_datalake_backend_service_TestView,
  testCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_TestSuiteView,
  testSuiteCreate_Body,
  testCodeBlockCreate_Body,
  userRoleCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_UserInformation,
  github_com_falqondev_reportdev_datalake_backend_service_InstitutionUserCreateFormByUser,
  userCreate_Body,
  userUpdate_Body,
  userDataAccessGroupCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_Argument,
  github_com_falqondev_reportdev_datalake_backend_service_RootInformation,
  github_com_falqondev_reportdev_datalake_backend_service_CodeBlock,
  github_com_falqondev_reportdev_datalake_backend_service_WorkflowView,
  github_com_falqondev_reportdev_datalake_backend_service_ArgumentForm,
  github_com_falqondev_reportdev_datalake_backend_service_CodeBlocksForm,
  workflowCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_WorkflowParent,
  github_com_falqondev_reportdev_datalake_backend_service_WorkflowVersionHistory,
};

const endpoints = makeApi([
  {
    method: "get",
    path: "/api/application-roles",
    alias: "applicationRoleList",
    description: `Lists all roles available`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "aproID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_ApplicationRole
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/audit-logs",
    alias: "auditLogsList",
    description: `Lists all logs`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "resource",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "userName",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "userEmail",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "action",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_AuditLogView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/audit-logs/download",
    alias: "auditLogsDownload",
    description: `Get logs export file`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "action",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "userName",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "resource",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "userEmail",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z.string(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/auth-media",
    alias: "mediaAuth",
    description: `Authenticate media server access`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z
          .object({ mediaURL: z.string().nullable() })
          .partial()
          .passthrough(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z.object({ jwt: z.string() }).passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/catalog-entity",
    alias: "catalogEntityList",
    description: `Lists all catalog entitys`,
    requestFormat: "json",
    parameters: [
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "tag",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "certID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "serviceType",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "fqn[eq]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "includeTemporary",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "domaID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "ctagID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "cataID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "fqn[contains]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "entityType",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_CatalogEntity
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/catalog-entity/:cataID",
    alias: "catalogEntityGet",
    description: `Get one catalog entity`,
    requestFormat: "json",
    parameters: [
      {
        name: "cataID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                beginSnapshot: z.number(),
                cascID: z.string(),
                cataID: z.string(),
                certification: z
                  .object({
                    certID: z.number(),
                    description: z.string(),
                    icon: z.string().nullish(),
                    name: z.string(),
                  })
                  .passthrough()
                  .nullish(),
                columns: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_CatalogColumn
                ),
                description: z.string(),
                domains: z.array(
                  z
                    .object({ domaID: z.number(), name: z.string() })
                    .passthrough()
                ),
                endSnapshot: z.number().nullish(),
                entityType: z.string(),
                fullyQualifiedName: z.string(),
                layeID: z.number().nullish(),
                layerDescription: z.string().nullish(),
                layerName: z.string().nullish(),
                path: z.string().nullish(),
                schemaName: z.string(),
                tableName: z.string(),
                tags: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_CatalogTag
                ),
                temporary: z.boolean(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/catalog-entity/:cataID",
    alias: "catalogEntityUpdate",
    description: `Update catalog entity`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: catalogEntityUpdate_Body,
      },
      {
        name: "cataID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                beginSnapshot: z.number(),
                cascID: z.string(),
                cataID: z.string(),
                certification: z
                  .object({
                    certID: z.number(),
                    description: z.string(),
                    icon: z.string().nullish(),
                    name: z.string(),
                  })
                  .passthrough()
                  .nullish(),
                columns: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_CatalogColumn
                ),
                description: z.string(),
                domains: z.array(
                  z
                    .object({ domaID: z.number(), name: z.string() })
                    .passthrough()
                ),
                endSnapshot: z.number().nullish(),
                entityType: z.string(),
                fullyQualifiedName: z.string(),
                layeID: z.number().nullish(),
                layerDescription: z.string().nullish(),
                layerName: z.string().nullish(),
                path: z.string().nullish(),
                schemaName: z.string(),
                tableName: z.string(),
                tags: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_CatalogTag
                ),
                temporary: z.boolean(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/catalog-navigation",
    alias: "catalogNavigationList",
    description: `Lists catalog navigation`,
    requestFormat: "json",
    parameters: [
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "includeTemporary",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "layeID",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "showTables",
        type: "Query",
        schema: z.boolean().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_CatalogNavigation
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/catalog-tag",
    alias: "catalogTagList",
    description: `Lists all catalog tags`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "tag",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "ctagID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "slug",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_CatalogTag
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/catalog-tag",
    alias: "catalogTagCreate",
    description: `Create one catalog tag`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({ tag: z.string() }).passthrough(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({ ctagID: z.number(), slug: z.string(), tag: z.string() })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/catalog-tag/:ctagID",
    alias: "catalogTagDelete",
    description: `Delete one catalog tag`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "ctagID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({ ctagID: z.number(), slug: z.string(), tag: z.string() })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/catalog-tag/:ctagID",
    alias: "catalogTagGet",
    description: `Get one catalog tag`,
    requestFormat: "json",
    parameters: [
      {
        name: "ctagID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({ ctagID: z.number(), slug: z.string(), tag: z.string() })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/certification",
    alias: "certificationList",
    description: `Lists all certifications`,
    requestFormat: "json",
    parameters: [
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "certID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_CertificationView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/certification",
    alias: "certificationCreate",
    description: `Create one certification`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: certificationCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                certID: z.number(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                icon: z.string().nullish(),
                name: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/certification/:certID",
    alias: "certificationDelete",
    description: `Delete one certification by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "certID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                certID: z.number(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                icon: z.string().nullish(),
                name: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/certification/:certID",
    alias: "certificationGet",
    description: `Get one certification`,
    requestFormat: "json",
    parameters: [
      {
        name: "certID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                certID: z.number(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                icon: z.string().nullish(),
                name: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/certification/:certID",
    alias: "certificationUpdate",
    description: `Update certification`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: certificationCreate_Body,
      },
      {
        name: "certID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                certID: z.number(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                icon: z.string().nullish(),
                name: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/chart-block",
    alias: "chartBlockList",
    description: `Lists all chart_blocks`,
    requestFormat: "json",
    parameters: [
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "chblID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "projID",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "includeRemoved",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "viewType",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_ChartBlockView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/chart-block",
    alias: "chartBlockCreate",
    description: `Create one chart_block`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: chartBlockCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                cachedData: z.unknown(),
                chartDefinition: z.unknown(),
                chblID: z.string(),
                code: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                lang: z.string(),
                name: z.string(),
                projID: z.string(),
                thumbnail: z.string(),
                updatedAt: z.string(),
                viewType: z.string().nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/chart-block/:chblID",
    alias: "chartBlockDelete",
    description: `Delete one chart_block by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "chblID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                cachedData: z.unknown(),
                chartDefinition: z.unknown(),
                chblID: z.string(),
                code: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                lang: z.string(),
                name: z.string(),
                projID: z.string(),
                thumbnail: z.string(),
                updatedAt: z.string(),
                viewType: z.string().nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/chart-block/:chblID",
    alias: "chartBlockGet",
    description: `Get one chart_block`,
    requestFormat: "json",
    parameters: [
      {
        name: "chblID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                cachedData: z.unknown(),
                chartDefinition: z.unknown(),
                chblID: z.string(),
                code: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                lang: z.string(),
                name: z.string(),
                projID: z.string(),
                thumbnail: z.string(),
                updatedAt: z.string(),
                viewType: z.string().nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/chart-block/:chblID",
    alias: "chartBlockUpdate",
    description: `Update chart_block`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: chartBlockCreate_Body,
      },
      {
        name: "chblID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                cachedData: z.unknown(),
                chartDefinition: z.unknown(),
                chblID: z.string(),
                code: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                lang: z.string(),
                name: z.string(),
                projID: z.string(),
                thumbnail: z.string(),
                updatedAt: z.string(),
                viewType: z.string().nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/code-blocks/:coblID",
    alias: "codeBlocksGet",
    description: `Get one code_blocks`,
    requestFormat: "json",
    parameters: [
      {
        name: "coblID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                coblID: z.string(),
                code: z.string(),
                createdAt: z.string(),
                database: z.string(),
                description: z.string(),
                executionOrder: z.number(),
                hash: z.string(),
                lang: z.string(),
                name: z.string(),
                parentID: z.string().nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/connect/playground",
    alias: "playgroundConnect",
    description: `Connect in playground websocket`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z.string(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/connection-secret",
    alias: "connectionSecretList",
    description: `Lists all connection_secrets`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "coseID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "accessLevel",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "credentialType",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "names",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_ConnectionSecretView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/connection-secret",
    alias: "connectionSecretCreate",
    description: `Create one connection_secret`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: connectionSecretCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                accessLevel: z.string(),
                connectString: z.string(),
                coseID: z.string(),
                createdAt: z.string(),
                credentials: z
                  .object({
                    accountID: z.string().nullish(),
                    accountName: z.string().nullish(),
                    chain: z.string().nullish(),
                    clientCertificatePath: z.string().nullish(),
                    clientID: z.string().nullish(),
                    clientSecret: z.string().nullish(),
                    database: z.string().nullish(),
                    endpoint: z.string().nullish(),
                    keyID: z.string().nullish(),
                    kmsKeyID: z.string().nullish(),
                    password: z.string().nullish(),
                    provider: z.string().nullish(),
                    region: z.string().nullish(),
                    scope: z.string().nullish(),
                    secret: z.string().nullish(),
                    sessionToken: z.string().nullish(),
                    tenantID: z.string().nullish(),
                    type: z.string(),
                    urlCompatibilityMode: z.boolean().nullish(),
                    urlStyle: z.string().nullish(),
                    useSsl: z.boolean().nullish(),
                    user: z.string().nullish(),
                  })
                  .passthrough(),
                deletedAt: z.string().nullish(),
                hidden: z.boolean(),
                host: z.string().nullish(),
                name: z.string(),
                port: z.number().nullish(),
                readOnly: z.boolean(),
                ssh: z
                  .object({
                    enabled: z.boolean(),
                    host: z.string().nullish(),
                    passphrase: z.string().nullish(),
                    port: z.number().nullish(),
                    privateKey: z.string().nullish(),
                    user: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
                ssl: z
                  .object({
                    ca: z.string().nullish(),
                    cert: z.string().nullish(),
                    enabled: z.boolean(),
                    key: z.string().nullish(),
                    mode: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/connection-secret/:coseID",
    alias: "connectionSecretDelete",
    description: `Delete one connection_secret by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "coseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                accessLevel: z.string(),
                connectString: z.string(),
                coseID: z.string(),
                createdAt: z.string(),
                credentials: z
                  .object({
                    accountID: z.string().nullish(),
                    accountName: z.string().nullish(),
                    chain: z.string().nullish(),
                    clientCertificatePath: z.string().nullish(),
                    clientID: z.string().nullish(),
                    clientSecret: z.string().nullish(),
                    database: z.string().nullish(),
                    endpoint: z.string().nullish(),
                    keyID: z.string().nullish(),
                    kmsKeyID: z.string().nullish(),
                    password: z.string().nullish(),
                    provider: z.string().nullish(),
                    region: z.string().nullish(),
                    scope: z.string().nullish(),
                    secret: z.string().nullish(),
                    sessionToken: z.string().nullish(),
                    tenantID: z.string().nullish(),
                    type: z.string(),
                    urlCompatibilityMode: z.boolean().nullish(),
                    urlStyle: z.string().nullish(),
                    useSsl: z.boolean().nullish(),
                    user: z.string().nullish(),
                  })
                  .passthrough(),
                deletedAt: z.string().nullish(),
                hidden: z.boolean(),
                host: z.string().nullish(),
                name: z.string(),
                port: z.number().nullish(),
                readOnly: z.boolean(),
                ssh: z
                  .object({
                    enabled: z.boolean(),
                    host: z.string().nullish(),
                    passphrase: z.string().nullish(),
                    port: z.number().nullish(),
                    privateKey: z.string().nullish(),
                    user: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
                ssl: z
                  .object({
                    ca: z.string().nullish(),
                    cert: z.string().nullish(),
                    enabled: z.boolean(),
                    key: z.string().nullish(),
                    mode: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/connection-secret/:coseID",
    alias: "connectionSecretGet",
    description: `Get one connection_secret`,
    requestFormat: "json",
    parameters: [
      {
        name: "coseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                accessLevel: z.string(),
                connectString: z.string(),
                coseID: z.string(),
                createdAt: z.string(),
                credentials: z
                  .object({
                    accountID: z.string().nullish(),
                    accountName: z.string().nullish(),
                    chain: z.string().nullish(),
                    clientCertificatePath: z.string().nullish(),
                    clientID: z.string().nullish(),
                    clientSecret: z.string().nullish(),
                    database: z.string().nullish(),
                    endpoint: z.string().nullish(),
                    keyID: z.string().nullish(),
                    kmsKeyID: z.string().nullish(),
                    password: z.string().nullish(),
                    provider: z.string().nullish(),
                    region: z.string().nullish(),
                    scope: z.string().nullish(),
                    secret: z.string().nullish(),
                    sessionToken: z.string().nullish(),
                    tenantID: z.string().nullish(),
                    type: z.string(),
                    urlCompatibilityMode: z.boolean().nullish(),
                    urlStyle: z.string().nullish(),
                    useSsl: z.boolean().nullish(),
                    user: z.string().nullish(),
                  })
                  .passthrough(),
                deletedAt: z.string().nullish(),
                hidden: z.boolean(),
                host: z.string().nullish(),
                name: z.string(),
                port: z.number().nullish(),
                readOnly: z.boolean(),
                ssh: z
                  .object({
                    enabled: z.boolean(),
                    host: z.string().nullish(),
                    passphrase: z.string().nullish(),
                    port: z.number().nullish(),
                    privateKey: z.string().nullish(),
                    user: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
                ssl: z
                  .object({
                    ca: z.string().nullish(),
                    cert: z.string().nullish(),
                    enabled: z.boolean(),
                    key: z.string().nullish(),
                    mode: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/connection-secret/:coseID",
    alias: "connectionSecretUpdate",
    description: `Update connection_secret`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: connectionSecretCreate_Body,
      },
      {
        name: "coseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                accessLevel: z.string(),
                connectString: z.string(),
                coseID: z.string(),
                createdAt: z.string(),
                credentials: z
                  .object({
                    accountID: z.string().nullish(),
                    accountName: z.string().nullish(),
                    chain: z.string().nullish(),
                    clientCertificatePath: z.string().nullish(),
                    clientID: z.string().nullish(),
                    clientSecret: z.string().nullish(),
                    database: z.string().nullish(),
                    endpoint: z.string().nullish(),
                    keyID: z.string().nullish(),
                    kmsKeyID: z.string().nullish(),
                    password: z.string().nullish(),
                    provider: z.string().nullish(),
                    region: z.string().nullish(),
                    scope: z.string().nullish(),
                    secret: z.string().nullish(),
                    sessionToken: z.string().nullish(),
                    tenantID: z.string().nullish(),
                    type: z.string(),
                    urlCompatibilityMode: z.boolean().nullish(),
                    urlStyle: z.string().nullish(),
                    useSsl: z.boolean().nullish(),
                    user: z.string().nullish(),
                  })
                  .passthrough(),
                deletedAt: z.string().nullish(),
                hidden: z.boolean(),
                host: z.string().nullish(),
                name: z.string(),
                port: z.number().nullish(),
                readOnly: z.boolean(),
                ssh: z
                  .object({
                    enabled: z.boolean(),
                    host: z.string().nullish(),
                    passphrase: z.string().nullish(),
                    port: z.number().nullish(),
                    privateKey: z.string().nullish(),
                    user: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
                ssl: z
                  .object({
                    ca: z.string().nullish(),
                    cert: z.string().nullish(),
                    enabled: z.boolean(),
                    key: z.string().nullish(),
                    mode: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/connection-secret/:name/verification",
    alias: "connectionSecretNameVerification",
    description: `Validate a duckdb secret names`,
    requestFormat: "json",
    parameters: [
      {
        name: "name",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "ignoreID",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                alternativeName: z.string(),
                name: z.string(),
                valid: z.boolean(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/connection-secret/default/tables-data",
    alias: "connectionSecretDefaultTablesDataList",
    description: `List data from all tables in the default database connection`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "fullQualifiedNames",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "keyword",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_TableData
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/dashboard",
    alias: "dashboardList",
    description: `Lists all dashboards`,
    requestFormat: "json",
    parameters: [
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "dashID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "includeRemoved",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "projID",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_DashboardView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/dashboard",
    alias: "dashboardCreate",
    description: `Create one dashboard`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: dashboardCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                dashID: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/dashboard/:dashID",
    alias: "dashboardDelete",
    description: `Delete one dashboard by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "dashID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                dashID: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/dashboard/:dashID",
    alias: "dashboardGet",
    description: `Get one dashboard`,
    requestFormat: "json",
    parameters: [
      {
        name: "dashID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                dashID: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/dashboard/:dashID",
    alias: "dashboardUpdate",
    description: `Update dashboard`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: dashboardCreate_Body,
      },
      {
        name: "dashID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                dashID: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/data-access-group",
    alias: "dataAccessGroupList",
    description: `Lists all data_access_roles`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "dagrID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_DataAccessGroupView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/data-access-group",
    alias: "dataAccessGroupCreate",
    description: `Create one data_access_group`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: dataAccessGroupCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                actions: z.array(z.string()),
                createdAt: z.string(),
                dagrID: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                name: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/data-access-group/:dagrID",
    alias: "dataAccessGroupDelete",
    description: `Delete one data_access_group by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "dagrID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                actions: z.array(z.string()),
                createdAt: z.string(),
                dagrID: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                name: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/data-access-group/:dagrID",
    alias: "dataAccessGroupGet",
    description: `Get one data_access_group`,
    requestFormat: "json",
    parameters: [
      {
        name: "dagrID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                actions: z.array(z.string()),
                createdAt: z.string(),
                dagrID: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                name: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/data-access-group/:dagrID",
    alias: "dataAccessGroupUpdate",
    description: `Update data_access_group`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: dataAccessGroupCreate_Body,
      },
      {
        name: "dagrID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                actions: z.array(z.string()),
                createdAt: z.string(),
                dagrID: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                name: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/documentations",
    alias: "documentationList",
    description: `Lists all documentations`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "title",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "docuIDs",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "tags",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_DocumentationView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/documentations",
    alias: "documentationCreate",
    description: `Create one documentation`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: documentationCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                content: z.string(),
                createdAt: z.string(),
                docuID: z.number(),
                tags: z.array(z.string()),
                title: z.string(),
                videoURL: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/documentations/:docuID",
    alias: "documentationDelete",
    description: `Delete one documentation by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "docuID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                content: z.string(),
                createdAt: z.string(),
                docuID: z.number(),
                tags: z.array(z.string()),
                title: z.string(),
                videoURL: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/documentations/:docuID",
    alias: "documentationGet",
    description: `Get one documentation`,
    requestFormat: "json",
    parameters: [
      {
        name: "docuID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                content: z.string(),
                createdAt: z.string(),
                docuID: z.number(),
                tags: z.array(z.string()),
                title: z.string(),
                videoURL: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/documentations/:docuID",
    alias: "documentationUpdate",
    description: `Update documentation`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: documentationCreate_Body,
      },
      {
        name: "docuID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                content: z.string(),
                createdAt: z.string(),
                docuID: z.number(),
                tags: z.array(z.string()),
                title: z.string(),
                videoURL: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/documents",
    alias: "documentList",
    description: `Lists all document`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "Limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "Offset",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_Document
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/documents",
    alias: "documentCreate",
    description: `Create one document`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: documentCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                docuID: z.string(),
                doteID: z.string(),
                formSchema: z.unknown(),
                permissionMode: z.string(),
                printSchema: z.unknown(),
                title: z.string(),
                uiSchema: z.unknown(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/documents/:docuID",
    alias: "documentDelete",
    description: `Delete one document by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "docuID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                docuID: z.string(),
                doteID: z.string(),
                formSchema: z.unknown(),
                permissionMode: z.string(),
                printSchema: z.unknown(),
                title: z.string(),
                uiSchema: z.unknown(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/documents/:docuID",
    alias: "documentGet",
    description: `Get one document`,
    requestFormat: "json",
    parameters: [
      {
        name: "docuID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                docuID: z.string(),
                doteID: z.string(),
                formSchema: z.unknown(),
                permissionMode: z.string(),
                printSchema: z.unknown(),
                title: z.string(),
                uiSchema: z.unknown(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/documents/:docuID",
    alias: "documentUpdate",
    description: `Update document`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: documentCreate_Body,
      },
      {
        name: "docuID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                docuID: z.string(),
                doteID: z.string(),
                formSchema: z.unknown(),
                permissionMode: z.string(),
                printSchema: z.unknown(),
                title: z.string(),
                uiSchema: z.unknown(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/domain",
    alias: "domainList",
    description: `Lists all domains`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "domaID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "parentID",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_DomainView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/domain",
    alias: "domainCreate",
    description: `Create one domain`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: domainCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                domaID: z.number(),
                name: z.string(),
                parentID: z.number().nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/domain/:domaID",
    alias: "domainDelete",
    description: `Delete one domain by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "domaID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                domaID: z.number(),
                name: z.string(),
                parentID: z.number().nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/domain/:domaID",
    alias: "domainGet",
    description: `Get one domain`,
    requestFormat: "json",
    parameters: [
      {
        name: "domaID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                domaID: z.number(),
                name: z.string(),
                parentID: z.number().nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/domain/:domaID",
    alias: "domainUpdate",
    description: `Update domain`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: domainCreate_Body,
      },
      {
        name: "domaID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                domaID: z.number(),
                name: z.string(),
                parentID: z.number().nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/error-report",
    alias: "errorReportList",
    description: `Lists all error_reports`,
    requestFormat: "json",
    parameters: [
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "erreID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_ErrorReportView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/error-report",
    alias: "errorReportCreate",
    description: `Create one error_report`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: errorReportCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                erreID: z.number(),
                filterRegex: z.string(),
                name: z.string(),
                notifiedUsers: z.array(z.string()),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/error-report/:erreID",
    alias: "errorReportDelete",
    description: `Delete one error_report by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "erreID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                erreID: z.number(),
                filterRegex: z.string(),
                name: z.string(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/error-report/:erreID",
    alias: "errorReportGet",
    description: `Get one error_report`,
    requestFormat: "json",
    parameters: [
      {
        name: "erreID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                erreID: z.number(),
                filterRegex: z.string(),
                name: z.string(),
                notifiedUsers: z.array(z.string()),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/error-report/:erreID",
    alias: "errorReportUpdate",
    description: `Update error_report`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: errorReportCreate_Body,
      },
      {
        name: "erreID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                erreID: z.number(),
                filterRegex: z.string(),
                name: z.string(),
                notifiedUsers: z.array(z.string()),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/file/upload",
    alias: "fileUpload",
    description: `Save one file on the server`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z.object({ filename: z.string() }).passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/integrations/oauth/:oainID",
    alias: "integrationGet",
    description: `Get integration auth`,
    requestFormat: "json",
    parameters: [
      {
        name: "oainID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "repoID",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "projID",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z.string(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/integrations/oauth/callback",
    alias: "integrationCallback",
    description: `callback integration auth`,
    requestFormat: "json",
    parameters: [
      {
        name: "code",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "state",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z.string(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/layer",
    alias: "layerList",
    description: `Lists all layers`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "layeID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_LayerView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/layer",
    alias: "layerCreate",
    description: `Create one layer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: layerCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                connections: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_LayerConnection
                ),
                coseID: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                displayOrder: z.number(),
                layeID: z.number(),
                name: z.string(),
                type: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/layer/:layeID",
    alias: "layerDelete",
    description: `Delete one layer by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "layeID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                connections: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_LayerConnection
                ),
                coseID: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                displayOrder: z.number(),
                layeID: z.number(),
                name: z.string(),
                type: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/layer/:layeID",
    alias: "layerGet",
    description: `Get one layer`,
    requestFormat: "json",
    parameters: [
      {
        name: "layeID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                connections: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_LayerConnection
                ),
                coseID: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                displayOrder: z.number(),
                layeID: z.number(),
                name: z.string(),
                type: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/layer/:layeID",
    alias: "layerUpdate",
    description: `Update layer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: layerCreate_Body,
      },
      {
        name: "layeID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                connections: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_LayerConnection
                ),
                coseID: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                displayOrder: z.number(),
                layeID: z.number(),
                name: z.string(),
                type: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/layer/:name/verification",
    alias: "layerNameVerification",
    description: `Validate a layer names`,
    requestFormat: "json",
    parameters: [
      {
        name: "name",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "ignoreID",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                alternativeName: z.string(),
                name: z.string(),
                valid: z.boolean(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/lineage",
    alias: "lineageGet",
    description: `Get one lineage`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "fqn",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                edges: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_EdgeView
                ),
                entity: z
                  .object({
                    database: z
                      .object({
                        description: z.string(),
                        displayName: z.string(),
                        fullyQualifiedName: z.string(),
                        id: z.string(),
                        name: z.string(),
                        type: z.string(),
                      })
                      .passthrough()
                      .nullish(),
                    databaseSchema: z
                      .object({
                        description: z.string(),
                        displayName: z.string(),
                        fullyQualifiedName: z.string(),
                        id: z.string(),
                        name: z.string(),
                        type: z.string(),
                      })
                      .passthrough()
                      .nullish(),
                    description: z.string().nullish(),
                    displayName: z.string().nullish(),
                    entityType: z.string(),
                    fullyQualifiedName: z.string(),
                    id: z.string(),
                    lineage: z
                      .array(
                        github_com_falqondev_reportdev_datalake_backend_service_EdgeView
                      )
                      .nullish(),
                    name: z.string(),
                    service: z
                      .object({
                        description: z.string(),
                        displayName: z.string(),
                        fullyQualifiedName: z.string(),
                        id: z.string(),
                        name: z.string(),
                        type: z.string(),
                      })
                      .passthrough()
                      .nullish(),
                    serviceType: z.string().nullish(),
                    tableColumns: z.array(
                      github_com_falqondev_reportdev_datalake_backend_service_CatalogColumn
                    ),
                    tableType: z.string().nullish(),
                    tags: z.array(
                      github_com_falqondev_reportdev_datalake_backend_service_CatalogTag
                    ),
                  })
                  .passthrough(),
                nodes: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_EntityView
                ),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/oauth-integration",
    alias: "oauthIntegrationList",
    description: `Lists all oauth_integrations`,
    requestFormat: "json",
    parameters: [
      {
        name: "type",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "oainID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_OauthIntegrationView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/oauth-integration",
    alias: "oauthIntegrationCreate",
    description: `Create one oauth_integration`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: oauthIntegrationCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                authScript: z.string(),
                callbackScript: z.string(),
                createdAt: z.string(),
                credential: z
                  .object({
                    clientID: z.string().nullable(),
                    clientSecret: z.string().nullable(),
                  })
                  .partial()
                  .passthrough(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                oainID: z.number(),
                refreshToken: z.string(),
                type: z.string(),
                userData: z
                  .object({ user_email: z.string().nullable() })
                  .partial()
                  .passthrough()
                  .nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/oauth-integration/:oainID",
    alias: "oauthIntegrationDelete",
    description: `Delete one oauth_integration by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "oainID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                authScript: z.string(),
                callbackScript: z.string(),
                createdAt: z.string(),
                credential: z
                  .object({
                    clientID: z.string().nullable(),
                    clientSecret: z.string().nullable(),
                  })
                  .partial()
                  .passthrough(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                oainID: z.number(),
                refreshToken: z.string(),
                type: z.string(),
                userData: z
                  .object({ user_email: z.string().nullable() })
                  .partial()
                  .passthrough()
                  .nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/oauth-integration/:oainID",
    alias: "oauthIntegrationGet",
    description: `Get one oauth_integration`,
    requestFormat: "json",
    parameters: [
      {
        name: "oainID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                authScript: z.string(),
                callbackScript: z.string(),
                createdAt: z.string(),
                credential: z
                  .object({
                    clientID: z.string().nullable(),
                    clientSecret: z.string().nullable(),
                  })
                  .partial()
                  .passthrough(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                oainID: z.number(),
                refreshToken: z.string(),
                type: z.string(),
                userData: z
                  .object({ user_email: z.string().nullable() })
                  .partial()
                  .passthrough()
                  .nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/oauth-integration/:oainID",
    alias: "oauthIntegrationUpdate",
    description: `Update oauth_integration`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: oauthIntegrationCreate_Body,
      },
      {
        name: "oainID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                authScript: z.string(),
                callbackScript: z.string(),
                createdAt: z.string(),
                credential: z
                  .object({
                    clientID: z.string().nullable(),
                    clientSecret: z.string().nullable(),
                  })
                  .partial()
                  .passthrough(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                oainID: z.number(),
                refreshToken: z.string(),
                type: z.string(),
                userData: z
                  .object({ user_email: z.string().nullable() })
                  .partial()
                  .passthrough()
                  .nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/one-time-login",
    alias: "oneTimeLoginCreate",
    description: `Create new oneTimeLogin`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: oneTimeLoginCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                otloID: z.number(),
                token: z.string(),
                userID: z.string(),
                validUntil: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/playground/:plseID/cancel-task/:runID",
    alias: "playgroundTaskCancel",
    description: `Cancel playground task`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "plseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "runID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                payload: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundCode
                ),
                plseID: z.string(),
                response: z
                  .array(z.object({}).partial().passthrough())
                  .nullish(),
                runID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/playground/:plseID/database/:dbname",
    alias: "playgroundDatabaseGet",
    description: `Get database information`,
    requestFormat: "json",
    parameters: [
      {
        name: "plseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "dbname",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                dbName: z.string(),
                plseID: z.string(),
                schemas: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_SchemaInfo
                ),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/playground/:plseID/pending-task",
    alias: "playgroundTaskGetPending",
    description: `Get playground pending tasks`,
    requestFormat: "json",
    parameters: [
      {
        name: "plseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                payload: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundCode
                ),
                plseID: z.string(),
                response: z
                  .array(z.object({}).partial().passthrough())
                  .nullish(),
                runID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/playground/:plseID/run-task",
    alias: "playgroundRunCreate",
    description: `Create one playground_session`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: playgroundRunCreate_Body,
      },
      {
        name: "plseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                payload: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundCode
                ),
                plseID: z.string(),
                response: z
                  .array(z.object({}).partial().passthrough())
                  .nullish(),
                runID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/playground/session",
    alias: "playgroundSessionList",
    description: `Lists all playground_sessions`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "plseID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "resourceID",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "resourceType",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_PlaygroundSessionView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/playground/session",
    alias: "playgroundSessionCreate",
    description: `Create one playground_session`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: playgroundSessionCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                expiresAt: z.string(),
                plseID: z.string(),
                resourceID: z.string(),
                resourceType: z.string(),
                tasks: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundTask
                ),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/playground/session/:plseID",
    alias: "playgroundSessionGet",
    description: `Get one playground_session`,
    requestFormat: "json",
    parameters: [
      {
        name: "plseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                expiresAt: z.string(),
                plseID: z.string(),
                resourceID: z.string(),
                resourceType: z.string(),
                tasks: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundTask
                ),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/project",
    alias: "projectList",
    description: `Lists all projects`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "projID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "includeRemoved",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_ProjectView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/project",
    alias: "projectCreate",
    description: `Create one project`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({ name: z.string() }).passthrough(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/project/:projID",
    alias: "projectDelete",
    description: `Delete one project by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "projID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/project/:projID",
    alias: "projectGet",
    description: `Get one project`,
    requestFormat: "json",
    parameters: [
      {
        name: "projID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/project/:projID",
    alias: "projectUpdate",
    description: `Update project`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({ name: z.string() }).passthrough(),
      },
      {
        name: "projID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/report",
    alias: "reportList",
    description: `Lists all reports`,
    requestFormat: "json",
    parameters: [
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "projID",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "schedulingEnabled",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "includeRemoved",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "reatID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "repoID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_ReportView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/report",
    alias: "reportCreate",
    description: `Create one report`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: reportCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                attachments: z
                  .object({
                    chartBlocks: z.array(z.string()),
                    dashboards: z.array(z.string()),
                  })
                  .passthrough(),
                chblID: z.string().nullish(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                enabled: z.boolean(),
                frequency: z.string(),
                message: z.string(),
                name: z.string(),
                oainID: z.number().nullish(),
                projID: z.string(),
                reatID: z.string(),
                recipients: z.array(z.string()),
                repoID: z.string(),
                reportFailureEmail: z.array(z.string()),
                sendMethod: z.string(),
                sendingIndividual: z.boolean(),
                subject: z.string(),
                templateRejectionReason: z.string().nullish(),
                templateSid: z.string().nullish(),
                templateStatus: z.string().nullish(),
                timezone: z.string(),
                useTextAttachments: z.boolean(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/report/:repoID",
    alias: "reportDelete",
    description: `Delete one report by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "repoID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                attachments: z
                  .object({
                    chartBlocks: z.array(z.string()),
                    dashboards: z.array(z.string()),
                  })
                  .passthrough(),
                chblID: z.string().nullish(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                enabled: z.boolean(),
                frequency: z.string(),
                message: z.string(),
                name: z.string(),
                oainID: z.number().nullish(),
                projID: z.string(),
                reatID: z.string(),
                recipients: z.array(z.string()),
                repoID: z.string(),
                reportFailureEmail: z.array(z.string()),
                sendMethod: z.string(),
                sendingIndividual: z.boolean(),
                subject: z.string(),
                templateRejectionReason: z.string().nullish(),
                templateSid: z.string().nullish(),
                templateStatus: z.string().nullish(),
                timezone: z.string(),
                useTextAttachments: z.boolean(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/report/:repoID",
    alias: "reportGet",
    description: `Get one report`,
    requestFormat: "json",
    parameters: [
      {
        name: "repoID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                attachments: z
                  .object({
                    chartBlocks: z.array(z.string()),
                    dashboards: z.array(z.string()),
                  })
                  .passthrough(),
                chblID: z.string().nullish(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                enabled: z.boolean(),
                frequency: z.string(),
                message: z.string(),
                name: z.string(),
                oainID: z.number().nullish(),
                projID: z.string(),
                reatID: z.string(),
                recipients: z.array(z.string()),
                repoID: z.string(),
                reportFailureEmail: z.array(z.string()),
                sendMethod: z.string(),
                sendingIndividual: z.boolean(),
                subject: z.string(),
                templateRejectionReason: z.string().nullish(),
                templateSid: z.string().nullish(),
                templateStatus: z.string().nullish(),
                timezone: z.string(),
                useTextAttachments: z.boolean(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/report/:repoID",
    alias: "reportUpdate",
    description: `Update report`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: reportCreate_Body,
      },
      {
        name: "repoID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                attachments: z
                  .object({
                    chartBlocks: z.array(z.string()),
                    dashboards: z.array(z.string()),
                  })
                  .passthrough(),
                chblID: z.string().nullish(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                enabled: z.boolean(),
                frequency: z.string(),
                message: z.string(),
                name: z.string(),
                oainID: z.number().nullish(),
                projID: z.string(),
                reatID: z.string(),
                recipients: z.array(z.string()),
                repoID: z.string(),
                reportFailureEmail: z.array(z.string()),
                sendMethod: z.string(),
                sendingIndividual: z.boolean(),
                subject: z.string(),
                templateRejectionReason: z.string().nullish(),
                templateSid: z.string().nullish(),
                templateStatus: z.string().nullish(),
                timezone: z.string(),
                useTextAttachments: z.boolean(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/term/accept",
    alias: "termAccept",
    description: `Accept a term`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({ termID: z.number() }).passthrough(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                accepted: z.boolean(),
                termID: z.number(),
                userID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/term/pending",
    alias: "termGetPending",
    description: `get all pending terms`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                terms: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_PendingTerm
                ),
                userID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/terms",
    alias: "termList",
    description: `Lists all terms`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "slug",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "title",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "active",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_Term
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/terms",
    alias: "termCreate",
    description: `Create one term`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: termCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                slug: z.string(),
                termID: z.number(),
                title: z.string(),
                type: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/terms/:termID",
    alias: "termDelete",
    description: `Delete one term by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "termID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                slug: z.string(),
                termID: z.number(),
                title: z.string(),
                type: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/terms/:termID",
    alias: "termGet",
    description: `Get one term`,
    requestFormat: "json",
    parameters: [
      {
        name: "termID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                slug: z.string(),
                termID: z.number(),
                title: z.string(),
                type: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/terms/:termID",
    alias: "termUpdate",
    description: `Update term`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: termCreate_Body,
      },
      {
        name: "termID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                slug: z.string(),
                termID: z.number(),
                title: z.string(),
                type: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/test",
    alias: "testList",
    description: `Lists all tests`,
    requestFormat: "json",
    parameters: [
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "testID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "fullQualifiedName",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_TestView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/test",
    alias: "testCreate",
    description: `Create one test`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: testCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                fullQualifiedName: z.string().nullish(),
                lastRun: z.string().nullish(),
                name: z.string(),
                suites: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_TestSuitData
                ),
                testID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/test-suite",
    alias: "testSuiteList",
    description: `Lists all test_suites`,
    requestFormat: "json",
    parameters: [
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "schedulingEnabled",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "tesuID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_TestSuiteView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/test-suite",
    alias: "testSuiteCreate",
    description: `Create one test_suite`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: testSuiteCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                name: z.string(),
                schedulingEnabled: z.boolean(),
                testIDs: z.array(z.string()),
                tesuID: z.number(),
                timezone: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/test-suite/:tesuID",
    alias: "testSuiteDelete",
    description: `Delete one test_suite by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "tesuID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                name: z.string(),
                schedulingEnabled: z.boolean(),
                testIDs: z.array(z.string()),
                tesuID: z.number(),
                timezone: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/test-suite/:tesuID",
    alias: "testSuiteGet",
    description: `Get one test_suite`,
    requestFormat: "json",
    parameters: [
      {
        name: "tesuID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                name: z.string(),
                schedulingEnabled: z.boolean(),
                testIDs: z.array(z.string()),
                tesuID: z.number(),
                timezone: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/test-suite/:tesuID",
    alias: "testSuiteUpdate",
    description: `Update test_suite`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: testSuiteCreate_Body,
      },
      {
        name: "tesuID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                name: z.string(),
                schedulingEnabled: z.boolean(),
                testIDs: z.array(z.string()),
                tesuID: z.number(),
                timezone: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/test/:testID",
    alias: "testDelete",
    description: `Delete one test by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "testID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                fullQualifiedName: z.string().nullish(),
                lastRun: z.string().nullish(),
                name: z.string(),
                suites: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_TestSuitData
                ),
                testID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/test/:testID",
    alias: "testGet",
    description: `Get one test`,
    requestFormat: "json",
    parameters: [
      {
        name: "testID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                fullQualifiedName: z.string().nullish(),
                lastRun: z.string().nullish(),
                name: z.string(),
                suites: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_TestSuitData
                ),
                testID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/test/:testID",
    alias: "testUpdate",
    description: `Update test`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: testCreate_Body,
      },
      {
        name: "testID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                fullQualifiedName: z.string().nullish(),
                lastRun: z.string().nullish(),
                name: z.string(),
                suites: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_TestSuitData
                ),
                testID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/test/:testID/code-block",
    alias: "testCodeBlockGet",
    description: `Get one test code block`,
    requestFormat: "json",
    parameters: [
      {
        name: "testID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                code: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                fullQualifiedName: z.string(),
                hash: z.string(),
                lang: z.string(),
                testID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/test/:testID/code-block",
    alias: "testCodeBlockCreate",
    description: `Create one test code block`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: testCodeBlockCreate_Body,
      },
      {
        name: "testID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                code: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                fullQualifiedName: z.string(),
                hash: z.string(),
                lang: z.string(),
                testID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/user-roles",
    alias: "userRoleCreate",
    description: `Create new user userRole`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: userRoleCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({ roles: z.array(z.string()), userID: z.string() })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/user-roles",
    alias: "userRoleUpdate",
    description: `Update one userRole`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: userRoleCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({ roles: z.array(z.string()), userID: z.string() })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/users",
    alias: "userList",
    description: `Lists all users`,
    requestFormat: "json",
    parameters: [
      {
        name: "email",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "aproID",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "instID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "deleted",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "applID",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "keywords",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "userID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_UserInformation
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/users",
    alias: "userCreate",
    description: `Create new user`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: userCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                applID: z.string(),
                auth2fa: z
                  .object({
                    methods: z.array(z.string()).nullish(),
                    required: z.boolean(),
                  })
                  .passthrough(),
                authenticationProvider: z.string(),
                avatar: z.string().nullish(),
                createdAt: z.string(),
                dataAccessRoles: z.array(z.string()),
                deletedAt: z.string().nullish(),
                email: z.string().nullish(),
                info: z.object({}).partial().passthrough(),
                name: z.string(),
                pushTokens: z.array(z.string()).nullish(),
                roles: z.array(z.string()),
                userID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/users/:userID",
    alias: "userDelete",
    description: `Delete one user`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "userID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                applID: z.string(),
                auth2fa: z
                  .object({
                    methods: z.array(z.string()).nullish(),
                    required: z.boolean(),
                  })
                  .passthrough(),
                authenticationProvider: z.string(),
                avatar: z.string().nullish(),
                createdAt: z.string(),
                dataAccessRoles: z.array(z.string()),
                deletedAt: z.string().nullish(),
                email: z.string().nullish(),
                info: z.object({}).partial().passthrough(),
                name: z.string(),
                pushTokens: z.array(z.string()).nullish(),
                roles: z.array(z.string()),
                userID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/users/:userID",
    alias: "userGet",
    description: `Get one user by userID`,
    requestFormat: "json",
    parameters: [
      {
        name: "userID",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                applID: z.string(),
                auth2fa: z
                  .object({
                    methods: z.array(z.string()).nullish(),
                    required: z.boolean(),
                  })
                  .passthrough(),
                authenticationProvider: z.string(),
                avatar: z.string().nullish(),
                createdAt: z.string(),
                dataAccessRoles: z.array(z.string()),
                deletedAt: z.string().nullish(),
                email: z.string().nullish(),
                info: z.object({}).partial().passthrough(),
                name: z.string(),
                pushTokens: z.array(z.string()).nullish(),
                roles: z.array(z.string()),
                userID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "patch",
    path: "/api/users/:userID",
    alias: "userUpdate",
    description: `Update one user`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: userUpdate_Body,
      },
      {
        name: "userID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                applID: z.string(),
                auth2fa: z
                  .object({
                    methods: z.array(z.string()).nullish(),
                    required: z.boolean(),
                  })
                  .passthrough(),
                authenticationProvider: z.string(),
                avatar: z.string().nullish(),
                createdAt: z.string(),
                dataAccessRoles: z.array(z.string()),
                deletedAt: z.string().nullish(),
                email: z.string().nullish(),
                info: z.object({}).partial().passthrough(),
                name: z.string(),
                pushTokens: z.array(z.string()).nullish(),
                roles: z.array(z.string()),
                userID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/users/:userID/data-access-group",
    alias: "userDataAccessGroupCreate",
    description: `Create one data_access_group`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: userDataAccessGroupCreate_Body,
      },
      {
        name: "userID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({ rules: z.array(z.string()), userID: z.string() })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/users/:userID/enable",
    alias: "userEnable",
    description: `Enable a deleted user`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "userID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                applID: z.string(),
                auth2fa: z
                  .object({
                    methods: z.array(z.string()).nullish(),
                    required: z.boolean(),
                  })
                  .passthrough(),
                authenticationProvider: z.string(),
                avatar: z.string().nullish(),
                createdAt: z.string(),
                dataAccessRoles: z.array(z.string()),
                deletedAt: z.string().nullish(),
                email: z.string().nullish(),
                info: z.object({}).partial().passthrough(),
                name: z.string(),
                pushTokens: z.array(z.string()).nullish(),
                roles: z.array(z.string()),
                userID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/workflow",
    alias: "workflowList",
    description: `Lists all workflows`,
    requestFormat: "json",
    parameters: [
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "validParentsFor",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "workID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "tag",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "woveID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "schedulingEnabled",
        type: "Query",
        schema: z.boolean().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_WorkflowView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/workflow",
    alias: "workflowCreate",
    description: `Create one workflow`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: workflowCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                arguments: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_Argument
                ),
                createdAt: z.string(),
                databases: z.array(z.string()),
                deletedAt: z.string().nullish(),
                dependsWorkIDs: z.array(z.string()),
                deployedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                hash: z.string(),
                name: z.string(),
                parentNames: z.array(z.string()),
                roots: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_RootInformation
                ),
                schedulingEnabled: z.boolean(),
                scripts: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_CodeBlock
                ),
                tag: z.string(),
                timezone: z.string(),
                wagrID: z.string().nullish(),
                wcegID: z.string(),
                wodeID: z.number().nullish(),
                workID: z.string(),
                woveID: z.number(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/workflow-tag-tree",
    alias: "workflowTagTreeGet",
    description: `Get workflow tags in tree`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                children: z.unknown(),
                name: z.string().nullish(),
                tag: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/workflow-tag/:workID",
    alias: "workflowTagUpdate",
    description: `Update workflow`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({ tag: z.string() }).passthrough(),
      },
      {
        name: "workID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({ tag: z.string(), workID: z.string() })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/workflow/:workID",
    alias: "workflowDelete",
    description: `Delete one workflow by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z
          .object({ forceAndUnlinkDependencies: z.boolean().nullable() })
          .partial()
          .passthrough(),
      },
      {
        name: "workID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                arguments: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_Argument
                ),
                createdAt: z.string(),
                databases: z.array(z.string()),
                deletedAt: z.string().nullish(),
                dependsWorkIDs: z.array(z.string()),
                deployedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                hash: z.string(),
                name: z.string(),
                parentNames: z.array(z.string()),
                roots: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_RootInformation
                ),
                schedulingEnabled: z.boolean(),
                scripts: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_CodeBlock
                ),
                tag: z.string(),
                timezone: z.string(),
                wagrID: z.string().nullish(),
                wcegID: z.string(),
                wodeID: z.number().nullish(),
                workID: z.string(),
                woveID: z.number(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/workflow/:workID",
    alias: "workflowGet",
    description: `Get one workflow`,
    requestFormat: "json",
    parameters: [
      {
        name: "workID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "woveID",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                arguments: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_Argument
                ),
                createdAt: z.string(),
                databases: z.array(z.string()),
                deletedAt: z.string().nullish(),
                dependsWorkIDs: z.array(z.string()),
                deployedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                hash: z.string(),
                name: z.string(),
                parentNames: z.array(z.string()),
                roots: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_RootInformation
                ),
                schedulingEnabled: z.boolean(),
                scripts: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_CodeBlock
                ),
                tag: z.string(),
                timezone: z.string(),
                wagrID: z.string().nullish(),
                wcegID: z.string(),
                wodeID: z.number().nullish(),
                workID: z.string(),
                woveID: z.number(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/workflow/:workID",
    alias: "workflowUpdate",
    description: `Update workflow`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: workflowCreate_Body,
      },
      {
        name: "workID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                arguments: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_Argument
                ),
                createdAt: z.string(),
                databases: z.array(z.string()),
                deletedAt: z.string().nullish(),
                dependsWorkIDs: z.array(z.string()),
                deployedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                hash: z.string(),
                name: z.string(),
                parentNames: z.array(z.string()),
                roots: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_RootInformation
                ),
                schedulingEnabled: z.boolean(),
                scripts: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_CodeBlock
                ),
                tag: z.string(),
                timezone: z.string(),
                wagrID: z.string().nullish(),
                wcegID: z.string(),
                wodeID: z.number().nullish(),
                workID: z.string(),
                woveID: z.number(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/workflow/:workID/deploy/:woveID",
    alias: "workflowDeploy",
    description: `Deploy Workflow version`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "woveID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "workID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                arguments: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_Argument
                ),
                createdAt: z.string(),
                databases: z.array(z.string()),
                deletedAt: z.string().nullish(),
                dependsWorkIDs: z.array(z.string()),
                deployedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                hash: z.string(),
                name: z.string(),
                parentNames: z.array(z.string()),
                roots: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_RootInformation
                ),
                schedulingEnabled: z.boolean(),
                scripts: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_CodeBlock
                ),
                tag: z.string(),
                timezone: z.string(),
                wagrID: z.string().nullish(),
                wcegID: z.string(),
                wodeID: z.number().nullish(),
                workID: z.string(),
                woveID: z.number(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/workflow/:workID/valid-parents",
    alias: "workflowValidParentList",
    description: `List all valid parent workflows`,
    requestFormat: "json",
    parameters: [
      {
        name: "workID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "invalidWorkIDs",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_WorkflowParent
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/workflow/:workID/version-history",
    alias: "workflowVersionHistoryList",
    description: `Get workflow version history`,
    requestFormat: "json",
    parameters: [
      {
        name: "workID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_WorkflowVersionHistory
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
